import React, { useEffect } from 'react';

import EventTracking from 'lib/EventTracking';

import BoxBody from 'components/BoxBody';
import Button from 'components/Button';
import CurrentUser from 'auth/CurrentUser';
import Divider from 'components/Divider';
import Heading from 'components/Heading';
import Row from 'components/Row';
import Text from 'components/Text';
import { TimeslotFrequencyInput } from 'globalTypes';
import { userReadableDayAndTimeFormatLocalTZ } from 'lib/dateTimeFormat';

type Props = {
  onClickBookSession: () => void;
  frequency?: TimeslotFrequencyInput;
  scheduleStartDateTime?: string;
  eventPrefix?: string;
};

export default function ConfirmCancelledRecurringSessionOrInstance({
  onClickBookSession,
  frequency = TimeslotFrequencyInput.once,
  scheduleStartDateTime,
  eventPrefix,
}: Props) {
  const currentUser = CurrentUser.getUser();
  const hasMultipleRecurringSessionsFeature = Boolean(
    currentUser && currentUser.hasFeature('add_recurring_sessions.web.dec-21')
  );

  const isInstanceOrOneOff = frequency === TimeslotFrequencyInput.once;
  const eventPrefixWithCoachingSessionType = isInstanceOrOneOff
    ? `${eventPrefix}.cancel_session_confirmation`
    : `${eventPrefix}.cancel_plan_confirmation`;

  function trackKey(key: string) {
    eventPrefix && EventTracking.trackKey(`${eventPrefixWithCoachingSessionType}.${key}`);
  }

  useEffect(() => {
    trackKey('view');
  }, []);

  function getSessionDescription() {
    switch (frequency) {
      case TimeslotFrequencyInput.weekly:
        return (
          <Text>
            All of your weekly sessions{' '}
            {scheduleStartDateTime && (
              <>
                for{' '}
                <Text weight="semibold">
                  every {userReadableDayAndTimeFormatLocalTZ(scheduleStartDateTime)}{' '}
                </Text>
              </>
            )}
            have been cancelled.
          </Text>
        );
      case TimeslotFrequencyInput.everyOtherWeek:
        return (
          <Text>
            All of your biweekly sessions{' '}
            {scheduleStartDateTime && (
              <>
                for{' '}
                <Text weight="semibold">
                  every other {userReadableDayAndTimeFormatLocalTZ(scheduleStartDateTime)}{' '}
                </Text>
              </>
            )}
            have been cancelled.
          </Text>
        );
    }
    // we don't need to provide additional details about the session in the cancellation
    // confirmation for one-offs or instances since the heading is informative enough.
    return undefined;
  }

  return (
    <>
      <BoxBody size="xsmall">
        <Row size="xsmall">
          <Heading level="2">
            Your {isInstanceOrOneOff ? 'session is' : 'sessions are'} cancelled
          </Heading>
        </Row>
        <Row size="xsmall">
          {getSessionDescription()}{' '}
          <Text>A confirmation has also been emailed to you for your records.</Text>
        </Row>
        <Divider size="medium" />
        <Row size="xsmall">
          <Heading level="4">
            <Text weight="normal">Add a session any time</Text>
          </Heading>
        </Row>
        <Row size="small">
          <Text>
            Whether you’re preparing for a test or need some extra help, you can book a new session
            whenever you need it.
          </Text>
        </Row>
        <Button
          eventKey={eventPrefix && `${eventPrefixWithCoachingSessionType}.add_new_session.click`}
          full
          onClick={onClickBookSession}>
          {hasMultipleRecurringSessionsFeature ? 'Add sessions' : 'Add new session'}
        </Button>
      </BoxBody>
    </>
  );
}
